import Head from "next/head";
import Link from "next/link";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock3,
  LineChart,
  Search,
  Sparkles,
  Target,
} from "lucide-react";
import Footer from "pagecomponents/newcomponents/Footer";
import Navbar from "pagecomponents/newcomponents/Navbar";
import SeoCtaLink from "@/components/SeoCtaLink";
import { comparisonPages } from "@/src/comparisons";
import { FREE_TRIAL_APPLICATION_CREDITS } from "@src/config/freeTrial";
import styles from "./style.module.css";

const siteUrl = "https://www.lifeshack.com";
const canonicalUrl = `${siteUrl}/compare/`;
const signupHref =
  "/signup/?utm_source=seo&utm_medium=organic&utm_campaign=alternatives&utm_content=compare";

export default function CompareIndexPage() {
  const title = "LifeShack Comparisons | Compare AI Job Search Tools";
  const description =
    "See how LifeShack compares with specific AI auto-apply, autofill, resume, and job-tracking tools by workflow, control, pricing fit, and application effort.";

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "LifeShack comparison pages",
    itemListElement: comparisonPages.map((page, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: `LifeShack vs ${page.competitor}`,
      url: `${siteUrl}/compare/${page.slug}/`,
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Compare",
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div className={styles.page}>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${siteUrl}/widget.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${siteUrl}/widget.png`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbJsonLd),
          }}
        />
      </Head>

      <Navbar />

      <main>
        <section className={`${styles.hero} ${styles.hubHero}`}>
          <div className={styles.heroInner}>
            <p className={styles.eyebrow}>AI job search tool comparisons</p>
            <h1>Compare LifeShack with the top AI auto-apply tools.</h1>
            <p className={styles.subheadline}>
              Find the best platform for getting from job search to submitted
              applications with fewer repetitive forms and clearer controls.
            </p>
            <div className={styles.heroActions}>
              <SeoCtaLink
                href={signupHref}
                pageType="comparison"
                placement="hero"
                className={styles.primaryCta}
              >
                Start with free application credits <ArrowRight size={18} />
              </SeoCtaLink>
              <a className={styles.secondaryCta} href="#comparison-pages">
                View comparisons
              </a>
            </div>
            <div className={styles.trialProof} aria-label="Introductory offer details">
              <span>
                <CheckCircle2 size={16} /> {FREE_TRIAL_APPLICATION_CREDITS} application credits included
              </span>
              <span>
                <CheckCircle2 size={16} /> No credit card required
              </span>
              <span>
                <CheckCircle2 size={16} /> Built for active job seekers
              </span>
            </div>
          </div>
        </section>

        <section className={styles.sectionAlt}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>How to choose</p>
              <h2>Pick the job-search workflow that fits how you apply.</h2>
              <p>
                Some tools help you write a better resume. Some help you fill
                forms faster. LifeShack is built for job seekers who want the
                application work itself to keep moving.
              </p>
              <p>
                See{" "}
                <Link href="/ai-auto-apply/">
                  <a className={styles.inlineLink}>how LifeShack Auto Apply works</a>
                </Link>{" "}
                to understand its setup and application controls.
              </p>
            </div>
            <div className={styles.searchIntentGrid}>
              <article className={styles.intentItem}>
                <Search size={24} />
                <h3>Autofill vs auto apply</h3>
                <p>
                  Decide whether you only need faster form filling or a tool
                  that can find, tailor, submit, and track applications.
                </p>
              </article>
              <article className={styles.intentItem}>
                <Target size={24} />
                <h3>Hands-on vs hands-off</h3>
                <p>
                  Compare tools for how much of the job-search process you still
                  have to manage yourself after setup.
                </p>
              </article>
              <article className={styles.intentItem}>
                <Clock3 size={24} />
                <h3>Proof before you pay</h3>
                <p>
                  LifeShack gives you a free-trial path so you can see whether
                  the workflow creates real application momentum.
                </p>
              </article>
              <article className={styles.intentItem}>
                <LineChart size={24} />
                <h3>Quality and control</h3>
                <p>
                  Look at controls like tailoring, pause/resume, and company
                  blocklists before letting any tool represent you.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.section} id="comparison-pages">
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Comparison pages</p>
              <h2>Pick a competitor and see where LifeShack fits.</h2>
              <p>
                Use these side-by-side breakdowns to understand where each tool
                fits and when LifeShack is the better choice.
              </p>
            </div>

            <div className={styles.hubGrid}>
              {comparisonPages.map((page) => (
                <article className={styles.hubCard} key={page.slug}>
                    <span>{page.eyebrow}</span>
                    <h3>LifeShack vs {page.competitor}</h3>
                    <p>{page.metaDescription}</p>
                    <Link href={`/compare/${page.slug}/`}>
                      <a className={styles.hubLink}>
                        Read comparison <ChevronRight size={16} />
                      </a>
                    </Link>
                    <Link href={`/alternatives/${page.slug}/`}>
                      <a className={styles.hubLink}>
                        See {page.competitor} alternatives{" "}
                        <ChevronRight size={16} />
                      </a>
                    </Link>
                </article>
              ))}
            </div>
            <div className={styles.verdictPanel}>
              <div>
                <p className={styles.eyebrow}>Category guide</p>
                <h3>
                  Compare all AI auto-apply tools by workflow, control, and
                  best-fit use case.
                </h3>
              </div>
              <Link href="/best-ai-auto-apply-tools/">
                <a className={styles.smallCta}>
                  View the complete guide <ChevronRight size={17} />
                </a>
              </Link>
            </div>
          </div>
        </section>

        <section className={styles.ctaBand}>
          <div className={styles.ctaInner}>
            <div>
              <p className={styles.eyebrow}>Ready to apply faster?</p>
              <h2>Try the LifeShack workflow before choosing a plan.</h2>
              <p>
                Start with {FREE_TRIAL_APPLICATION_CREDITS} free application
                credits and see whether AI-assisted auto apply fits your job
                search. No credit card is required.
              </p>
            </div>
            <SeoCtaLink
              href={signupHref}
              pageType="comparison"
              placement="bottom_cta"
              className={styles.primaryCta}
            >
              Start with free credits <Sparkles size={18} />
            </SeoCtaLink>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

CompareIndexPage.isPublicSeoPage = true;
